import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { pickAnchor, type AnchorCandidate } from './pickAnchor'

/**
 * Arm-and-click element picker.
 *
 * A transparent full-screen layer captures the click so the app underneath does
 * not also act on it — clicking "Export to Excel" to anchor a step must not
 * actually export. `elementFromPoint` is used to find what is *under* the layer,
 * which is why the layer can stay fully interactive without swallowing the target.
 */
export type ElementPickerProps = {
  onPick: (candidate: AnchorCandidate) => void
  onCancel: () => void
}

export function ElementPicker({ onPick, onCancel }: ElementPickerProps) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [hovered, setHovered] = useState<Element | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel() }
    }, { signal: controller.signal })
    return () => controller.abort()
  }, [onCancel])

  const under = (e: React.MouseEvent): Element | null => {
    const layer = e.currentTarget as HTMLElement
    const prior = layer.style.pointerEvents
    layer.style.pointerEvents = 'none'
    const el = document.elementFromPoint(e.clientX, e.clientY)
    layer.style.pointerEvents = prior
    return el
  }

  return createPortal(
    <>
      <div
        className="fg-author-pick-overlay"
        onMouseMove={(e) => {
          const el = under(e)
          if (!el || el === hovered) return
          setHovered(el)
          setRect(el.getBoundingClientRect())
        }}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          const el = under(e)
          if (el) onPick(pickAnchor(el))
        }}
      />
      {rect ? (
        <div
          className="fg-author-pick-outline"
          style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height }}
        />
      ) : null}
      <div className="fg-author-pick-hint">Click an element to anchor this step · Esc to cancel</div>
    </>,
    document.body,
  )
}
